import React, { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Tier } from './Tier';
import { UnassignedItems } from './UnassignedItems';
import { useTierListStore } from '../store/tierListStore';
import { Plus, ArrowLeft, Download } from 'lucide-react';
import { ItemModal } from './ItemModal';
import { Item } from './Item';
import { DeleteConfirmationModal } from './DeleteConfirmationModal';

interface TierListProps {
  id: string;
}

export const TierList: React.FC<TierListProps> = ({ id }) => {
  const { state, dispatch } = useTierListStore();
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [deleteModalData, setDeleteModalData] = useState<{ id: string; title: string } | null>(null);
  const tierList = state.tierLists.find((list) => list.id === id);

  if (!tierList) {
    window.location.hash = '';
    return null;
  }

  const handleAddItem = (data: { name: string; image?: string }) => {
    dispatch({
      type: 'ADD_ITEM',
      payload: {
        tierListId: id,
        item: data,
      },
    });
  };

  const handleDeleteItem = (itemId: string) => {
    dispatch({
      type: 'DELETE_ITEM',
      payload: {
        tierListId: id,
        itemId,
      },
    });
  };

  const addTier = () => {
    dispatch({
      type: 'ADD_TIER',
      payload: {
        tierListId: id,
        tier: {
          name: 'New Tier',
          color: '#' + Math.floor(Math.random()*16777215).toString(16),
        },
      },
    });
  };

  const handleDragStart = (event: { active: { id: string; data: { current: any } } }) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;

    const activeType = active.data.current?.type;

    // Handle tier reordering
    if (activeType === 'tier') {
      const oldIndex = tierList.tiers.findIndex((t) => t.id === active.id);
      const newIndex = tierList.tiers.findIndex((t) => t.id === over.id);

      if (oldIndex !== newIndex) {
        const newTiers = [...tierList.tiers];
        const [movedTier] = newTiers.splice(oldIndex, 1);
        newTiers.splice(newIndex, 0, movedTier);

        dispatch({
          type: 'REORDER_TIERS',
          payload: {
            tierListId: id,
            tierIds: newTiers.map((t) => t.id),
          },
        });
      }
      return;
    }

    // Handle item deletion
    if (over.data.current?.isTrash) {
      handleDeleteItem(active.id as string);
      return;
    }

    // Handle item movement
    const fromTierId = active.data.current?.tierId;
    const toTierId = over.data.current?.tierId;

    if (fromTierId === toTierId && fromTierId !== null) {
      const tier = tierList.tiers.find((t) => t.id === fromTierId);
      if (!tier) return;

      const oldIndex = tier.items.findIndex((item) => item.id === active.id);
      const newIndex = tier.items.findIndex((item) => item.id === over.id);

      if (oldIndex !== newIndex && newIndex !== -1) {
        const newItems = [...tier.items];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);

        dispatch({
          type: 'REORDER_ITEMS',
          payload: {
            tierListId: id,
            tierId: fromTierId,
            itemIds: newItems.map((item) => item.id),
          },
        });
      }
      return;
    }

    // Handle moving items between tiers
    dispatch({
      type: 'MOVE_ITEM',
      payload: {
        tierListId: id,
        itemId: active.id as string,
        fromTierId,
        toTierId,
      },
    });
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setEditTitle(tierList.title);
  };

  const handleSaveTitle = () => {
    if (editTitle.trim()) {
      dispatch({
        type: 'UPDATE_TIER_LIST',
        payload: {
          id: tierList.id,
          title: editTitle.trim(),
        },
      });
    }
    setIsEditing(false);
  };

  const handleDeleteTierList = () => {
    setDeleteModalData({ id: tierList.id, title: tierList.title });
  };

  const confirmDelete = () => {
    if (deleteModalData) {
      dispatch({
        type: 'DELETE_TIER_LIST',
        payload: { id: deleteModalData.id },
      });
      window.location.hash = '';
      setDeleteModalData(null);
    }
  };

  const handleExport = () => {
    // Create a deep copy of the tier list data
    const exportData = {
      title: tierList.title,
      tiers: tierList.tiers.map(tier => ({
        id: tier.id,
        name: tier.name,
        color: tier.color,
        items: tier.items.map(item => ({
          id: item.id,
          name: item.name,
          image: item.image,
          description: item.description
        }))
      })),
      unassignedItems: tierList.unassignedItems.map(item => ({
        id: item.id,
        name: item.name,
        image: item.image,
        description: item.description
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tierList.title.toLowerCase().replace(/\s+/g, '-')}-tier-list.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getActiveItem = () => {
    if (!activeId) return null;

    // Check if it's a tier
    const activeTier = tierList.tiers.find((t) => t.id === activeId);
    if (activeTier) return { type: 'tier', tier: activeTier };

    // Check in all tiers
    for (const tier of tierList.tiers) {
      const item = tier.items.find((i) => i.id === activeId);
      if (item) return { type: 'item', item, tierId: tier.id };
    }

    // Check in unassigned items
    const unassignedItem = tierList.unassignedItems.find(
      (i) => i.id === activeId
    );
    if (unassignedItem) return { type: 'item', item: unassignedItem, tierId: null };

    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-3 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-4">
          <a
            href="#"
            className="p-2 -ml-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </a>
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={handleSaveTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') setIsEditing(false);
                }}
                className="text-xl sm:text-2xl font-bold px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white w-full"
                autoFocus
              />
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {tierList.title}
              </h2>
              <button
                onClick={handleStartEditing}
                className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                  <path d="m15 5 4 4"/>
                </svg>
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 sm:ml-auto">
          <button
            onClick={handleDeleteTierList}
            className="px-3 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors text-sm"
          >
            Delete List
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1 px-3 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors text-sm"
          >
            <Download size={16} /> Export
          </button>
          <button
            onClick={addTier}
            className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
          >
            <Plus size={16} /> Add Tier
          </button>
          <button
            onClick={() => setIsItemModalOpen(true)}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors text-sm"
          >
            <Plus size={16} /> Add Item
          </button>
        </div>
      </div>

      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        collisionDetection={rectIntersection}
      >
        <SortableContext
          items={tierList.tiers.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3 sm:space-y-4">
            {tierList.tiers.map((tier) => (
              <Tier
                key={tier.id}
                tier={tier}
                tierListId={id}
                onDelete={() =>
                  dispatch({
                    type: 'DELETE_TIER',
                    payload: { tierListId: id, tierId: tier.id },
                  })
                }
              />
            ))}
            <UnassignedItems items={tierList.unassignedItems} onDeleteItem={handleDeleteItem} />
          </div>
        </SortableContext>

        <DragOverlay dropAnimation={null}>
          {activeId ? (
            (() => {
              const activeItem = getActiveItem();
              if (!activeItem) return null;

              if (activeItem.type === 'tier') {
                return <Tier tier={activeItem.tier} tierListId={id} onDelete={() => {}} />;
              }

              return (
                <div style={{ 
                  cursor: 'grabbing',
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                  pointerEvents: 'none'
                }}>
                  <Item item={activeItem.item} tierId={activeItem.tierId} />
                </div>
              );
            })()
          ) : null}
        </DragOverlay>
      </DndContext>

      <ItemModal
        isOpen={isItemModalOpen}
        onClose={() => setIsItemModalOpen(false)}
        onSubmit={handleAddItem}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalData !== null}
        onClose={() => setDeleteModalData(null)}
        onConfirm={confirmDelete}
        title={deleteModalData?.title || ''}
      />
    </div>
  );
};